import { GWCA } from '~/lib/ca-constants';
import { GWUtils } from '~/utils/ca-utils';
import { AddressSuggest } from '~/lib/address-suggest';
import { AddressValidate } from '~/lib/address-validate';
import { stateCodeMapping } from '~/lib/constants';
import { validatePhone } from '~/utils/address-utils';

customElements.define(
  'gw-address-form',
  class GWAddressForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form');
      this.type = this.getAttribute('type');
      this.addressId = this.getAttribute('address-id');

      this.addressSuggest = null;
      this.addressValidate = null;
    }

    connectedCallback() {
      this._handleFormSubmit();
      this._initAddressAutocomplete();
      this._initPhoneFormatting();

      if (this.type === 'edit') {
        const deleteAddressButton = this.querySelector('[data-delete-address]');
        if (deleteAddressButton) {
          deleteAddressButton.addEventListener('click', () => {
            this._deleteAddress();
          });
        }
      }
    }

    disconnectedCallback() {
      if (this.addressSuggest) {
        this.addressSuggest.destroy();
        this.addressSuggest = null;
      }
    }

    get addressFields() {
      return {
        firstName: this.form.querySelector('[name="firstName"]'),
        lastName: this.form.querySelector('[name="lastName"]'),
        address1: this.form.querySelector('[name="address1"]'),
        address2: this.form.querySelector('[name="address2"]'),
        city: this.form.querySelector('[name="city"]'),
        country: this.form.querySelector('[name="country"]'),
        province: this.form.querySelector('[name="province"]'),
        zip: this.form.querySelector('[name="zip"]'),
        phone: this.form.querySelector('[name="phone"]'),
      };
    }

    _initAddressAutocomplete() {
      customElements.whenDefined('sl-input').then(() => {
        const addressInput = this.addressFields.address1;
        if (!addressInput) return;

        const shadowInput = addressInput.shadowRoot?.querySelector('input');
        const targetInput = shadowInput || addressInput;

        const wrapper = document.createElement('div');
        wrapper.className = 'address-autocomplete-container';
        wrapper.style.position = 'relative';
        addressInput.parentNode.insertBefore(wrapper, addressInput);
        wrapper.appendChild(addressInput);

        this.addressSuggest = new AddressSuggest(targetInput, {
          onSelect: (addressData) => this._fillInAddress(addressData),
        }).attach();
      });
    }

    _fillInAddress(addressData) {
      const fields = this.addressFields;
      const city = addressData.City || '';
      const state = addressData.Province || addressData.ProvinceCode || '';
      const postcode = addressData.PostalCode || '';

      if (fields.city) fields.city.value = city;
      if (fields.province) fields.province.value = stateCodeMapping[state] || state;
      if (fields.zip) fields.zip.value = postcode;

      if (fields.address2) {
        fields.address2.focus();
      }
    }

    _initPhoneFormatting() {
      customElements.whenDefined('sl-input').then(() => {
        const phoneInput = this.addressFields.phone;
        if (!phoneInput) return;

        phoneInput.addEventListener('sl-input', (event) => {
          const input = event.target;
          const x = input.value.replace(/\D/g, '').match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
          input.value = !x[2] ? x[1] : '(' + x[1] + ') ' + x[2] + (x[3] ? '-' + x[3] : '');
        });
      });
    }

    async _handleFormSubmit() {
      await customElements.whenDefined('sl-input');

      this.form.addEventListener(
        'sl-invalid',
        (event) => {
          event.preventDefault();
          this._clearErrorMessages();

          this.form.querySelectorAll('sl-input').forEach((element) => {
            if (element.hasAttribute('data-invalid')) {
              const errorBox = document.createElement('div');
              errorBox.slot = 'help-text';
              errorBox.classList.add('gw-error-message');
              errorBox.textContent = element.validationMessage;
              element.appendChild(errorBox);
            }
          });

          event.target.focus();
        },
        { capture: true }
      );

      this.form.addEventListener('submit', async (event) => {
        event.preventDefault();

        const formData = new FormData(this.form);
        const address = Object.fromEntries(formData.entries());

        await this._validateAndSubmit(address);
      });
    }

    async _validateAndSubmit(address) {
      this._clearErrorMessages();

      const phoneField = this.addressFields.phone;
      if (phoneField && phoneField.value?.trim()) {
        const phoneResult = await validatePhone(phoneField);
        if (phoneResult.error) {
          this._displayFieldError(phoneField, phoneResult.error);
          phoneField.focus();
          return;
        }
      }

      const addressToValidate = {
        address1: address.address1?.trim() || '',
        address2: address.address2?.trim() || '',
        city: address.city?.trim() || '',
        state: address.province?.trim() || '',
        zip: address.zip?.trim() || '',
        country: address.country?.trim() || 'United States'
      };

      this.addressValidate = new AddressValidate({
        container: this.form,
        onApply: (suggestedAddress) => this._applyAddressSuggestion(suggestedAddress),
        inShadowRoot: true
      });

      const result = await this.addressValidate.validateAndConfirm(addressToValidate);

      if (result.proceed) {
        const finalAddress = { ...address };
        if (result.address) {
          finalAddress.address1 = result.address.address1;
          finalAddress.address2 = result.address.address2 || '';
          finalAddress.city = result.address.city;
          finalAddress.province = result.address.state;
          finalAddress.zip = result.address.zip;
        }
        await this._submitAddressForm(finalAddress);
      }
    }

    _applyAddressSuggestion(suggestedAddress) {
      const fields = this.addressFields;
      if (fields.address1) fields.address1.value = suggestedAddress.address1;
      if (fields.address2) fields.address2.value = suggestedAddress.address2 || '';
      if (fields.city) fields.city.value = suggestedAddress.city;
      if (fields.province) fields.province.value = suggestedAddress.state;
      if (fields.zip) fields.zip.value = suggestedAddress.zip;
    }

    _displayFieldError(field, message) {
      const errorBox = document.createElement('div');
      errorBox.slot = 'help-text';
      errorBox.classList.add('gw-error-message');
      errorBox.textContent = message;
      field.appendChild(errorBox);
      field.setAttribute('data-user-invalid', '');
    }

    async _submitAddressForm(address) {
      try {
        this._clearErrorMessages();
        this.form.querySelector('[data-loader]').hidden = false;

        if (address.province) address.province = address.province.replaceAll('_', ' ');
        if (address.country) address.country = address.country.replaceAll('_', ' ');

        let isDefault = address.isDefault;
        delete address.isDefault;

        let response = null;

        if (this.type === 'edit') {
          response = await GWUtils.request(GWCA.ENDPOINTS.ADDRESS.EDIT, {
            address,
            addrID: this.addressId,
            isDefault: isDefault,
          });
        } else {
          response = await GWUtils.request(GWCA.ENDPOINTS.ADDRESS.ADD, {
            address,
            isDefault: isDefault,
          });
        }

        if (response.error) {
          throw new Error(response.error);
        }

        this.form.closest('sl-drawer').hide();

        GWUtils.updateAjaxContent(['[data-gw-addresses-list]', '[data-gw-addresses-carousel]']);
      } catch (error) {
        console.error(error);
      } finally {
        this.form.querySelector('[data-loader]').hidden = true;
      }
    }

    async _deleteAddress() {
      try {
        this.querySelector('[data-delete-address]').querySelector('[data-loader]').hidden = false;

        let response = await GWUtils.request(GWCA.ENDPOINTS.ADDRESS.REMOVE, {
          addrID: this.addressId,
        });

        if (response.error) {
          throw new Error(response.error);
        }

        this.form.closest('sl-drawer').hide();

        await GWUtils.app.updateContent(['[data-gw-addresses-list]', '[data-gw-addresses-carousel]']);
      } catch (error) {
        console.error(error);
      }
    }

    _clearErrorMessages() {
      this.form.querySelectorAll('.gw-error-message').forEach((errorBox) => errorBox.remove());
      this.form.querySelectorAll('[data-user-invalid]').forEach((field) => {
        field.removeAttribute('data-user-invalid');
      });
    }
  }
);
